import { useState } from 'react';
import type { OnboardingStepProps } from '../../registration';
import { ContinueButton } from '../secure-your-account/secure-your-account.styled';
import { SelectField } from '../../../../components/select/select';
import { timezoneOptions } from './timezone-options';
import {
  Heading,
  SubHeading,
  ButtonsContainer,
  ContentSection,
  InputsContainer,
  CenterColumn,
  SmsConsentRow,
  SmsConsentCheckbox,
  SmsConsentLabel,
  SmsConsentError,
  StyledLink,
} from './personalize-your-experience.styled';
import { VSpace } from '../../../chat/settings/account/account-settings';
import { UniversalSpinner } from '../../../../components/universal-spinner/universal-spinner';
import { useAuth } from '../../../../contexts/auth/auth-context';

export const PersonalizeStep: React.FC<OnboardingStepProps> = ({ goNext }) => {
  const { setRegistration, loading } = useAuth();

  /* ──────────────────────────────────────────────────────────
     TIME-ZONE HANDLING
     ────────────────────────────────────────────────────────── */
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [timezone, setTimezone] = useState<string>(browserTz);

  /* If browser zone isn't in the curated list, prepend it once. */
  const tzInList = timezoneOptions.some((o) => o.value === browserTz);
  const combinedTzOptions = tzInList
    ? timezoneOptions
    : [{ value: browserTz, label: `${browserTz} (detected)` }, ...timezoneOptions];

  /* ──────────────────────────────────────────────────────────
     NOTIFICATION METHOD / CONSENT
     ────────────────────────────────────────────────────────── */
  const [notificationMethod, setMethod] = useState<'sms' | 'email' | 'both'>('both');
  const [agreeSms, setAgreeSms] = useState(false);
  const [consentError, setConsentError] = useState('');

  const notificationOptions = [
    { value: 'sms', label: 'Text / SMS' },
    { value: 'email', label: 'Email notifications' },
    { value: 'both', label: 'Both' },
  ];

  const needsSmsConsent = notificationMethod !== 'email';
  const allValid = notificationMethod && (!needsSmsConsent || agreeSms);

  /* ──────────────────────────────────────────────────────────
     CONTINUE HANDLER
     ────────────────────────────────────────────────────────── */
  const handleContinue = async () => {
    if (needsSmsConsent && !agreeSms) {
      setConsentError('Please agree to receive SMS updates to continue.');
      return;
    }

    setRegistration({
      timezone, // picked (or detected) value
      notificationMethod,
      smsConsent: agreeSms,
    });
    goNext();
  };

  /* ──────────────────────────────────────────────────────────
     RENDER
     ────────────────────────────────────────────────────────── */
  return (
    <ContentSection>
      <CenterColumn>
        <VSpace $h={32} />

        <Heading>Personalize your experience.</Heading>
        <VSpace $h={16} />

        <SubHeading>
          Choose your time&nbsp;zone and how you'd like to get updates. You can refine these anytime
          in the app.
        </SubHeading>
      </CenterColumn>

      <InputsContainer>
        {/* Editable time-zone dropdown */}
        <SelectField
          id="timezone"
          label="Timezone"
          value={timezone}
          onChange={(val) => setTimezone(val as string)}
          options={combinedTzOptions}
        />

        {/* Notification-method dropdown */}
        <div>
          <SelectField
            id="notification-method"
            label="Notification method"
            value={notificationMethod}
            onChange={(val) => {
              setMethod(val as typeof notificationMethod);

              // Reset consent state when switching to email-only
              if (val === 'email') {
                setAgreeSms(false);
                setConsentError('');
              }
            }}
            options={notificationOptions}
          />

          {/* SMS consent checkbox (only if needed) */}
          {needsSmsConsent && (
            <SmsConsentRow>
              <SmsConsentCheckbox
                type="checkbox"
                id="sms-consent"
                checked={agreeSms}
                onChange={(e) => {
                  setAgreeSms(e.target.checked);
                  if (e.target.checked) setConsentError('');
                }}
              />

              <SmsConsentLabel htmlFor="sms-consent">
                I agree to receive SMS updates from Mailander. Frequency varies. Msg &amp; Data
                rates may apply. Reply STOP to opt out. See our{' '}
                <StyledLink
                  href="https://mailander.com/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms
                </StyledLink>{' '}
                and{' '}
                <StyledLink
                  href="https://mailander.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy&nbsp;policy
                </StyledLink>
                .
              </SmsConsentLabel>
            </SmsConsentRow>
          )}

          {consentError && <SmsConsentError>{consentError}</SmsConsentError>}
        </div>
      </InputsContainer>

      <ButtonsContainer>
        <ContinueButton disabled={!allValid} onClick={handleContinue}>
          {loading ? <UniversalSpinner /> : 'Continue'}
        </ContinueButton>
      </ButtonsContainer>
    </ContentSection>
  );
};

/* ───────── shell flags ───────── */
(PersonalizeStep as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
(PersonalizeStep as any).fill = true;
(PersonalizeStep as any).smallLogo = true;
(PersonalizeStep as any).progress = 'Personalize';
